import {Routes} from '@angular/router';
import {AuthGuard, LoginGuard} from './services/authentication.service';
import {CanCreateGuard, NavGuard, NavGuardSettings} from './services/account-permissions.service';
// Views components
import {HomeComponent} from './views/home/home.component';
import {LoginComponent} from './views/login/login.component';
import {DirectoryComponent} from './views/directory/directory.component';
import {ParcelsComponent} from './views/parcels/parcels.component';
import {IncidentReportsComponent} from './views/incident-reports/incident-reports.component';
import {ServiceRequestComponent} from './views/service-request/service-request.component';

import {BasicLayoutComponent} from './layouts/basic/basic.component';
import {BlankLayoutComponent} from './layouts/blank/blank.component';
import {DirectoryItemComponent} from './views/directory-item/directory-item.component';

import {InfoComponent} from './views/directory-item/info/info.component';
import {ResidentsComponent} from './views/directory-item/residents/residents.component';
import {ParkingSpotsComponent} from './views/directory-item/parking-spots/parking-spots.component';
import {LockersComponent} from './views/directory-item/lockers/lockers.component';
import {VehiclesComponent} from './views/directory-item/vehicles/vehicles.component';
import {PetsComponent} from './views/directory-item/pets/pets.component';
import {BikeRacksComponent} from './views/directory-item/bike-racks/bike-racks.component';
import {ItemActivityComponent} from './views/directory-item/activity/activity.component';

import {EmailComponent} from './views/email/email.component';
import {ComposeEmailComponent} from './views/email/compose-email/compose-email.component';
import {SettingsComponent} from './views/settings/settings.component';
import {ThemeListComponent} from './views/admin/themes/theme-list.component';
import {ManagementCompanyComponent} from './views/admin/management-company/management-company.component';
import {PropertyComponent} from './views/admin/property/property.component';
import {KeyLogComponent} from './views/key-log/key-log.component';
import {DocumentsComponent} from './views/documents/documents.component';
import {PassOnLogComponent} from './views/pass-on-log/pass-on-log.component';
import {DailyReportsComponent} from './views/daily-reports/daily-reports.component';
import {UserRoleComponent} from './views/admin/user-role/user-role.component';
import {UserComponent} from './views/admin/user/user.component';
import {ClassifiedsComponent} from './views/classifieds/classifieds.component';
import {NoticesComponent} from './views/notices/notices.component';
import {ProfileComponent} from './views/profile/profile.component';
import {AmenitiesComponent} from './views/amenities/amenities.component';
import {SettingsAmenitiesComponent} from './views/settings-amenities/settings-amenities.component';
import {WebsiteBuilderComponent} from './views/website-builder/website-builder.component';
import {SettingsParkingPassesComponent} from './views/settings-parking-passes/settings-parking-passes.component';
import {ParkingPassComponent} from './views/parking-pass/parking-pass.component';
import {ConfirmDeactivateGuard} from './services/email.service';
import {SettingsParcelsComponent} from './views/settings-parcels/settings-parcels.component';
import {ReportGeneratorComponent} from './views/report-generator/report-generator.component';
import {ExitPageGuard} from './exit.page.guard';
import {ConfirmReportModalDeactivateGuard} from './services/report-generator.service';
import {BuildingSetupComponent} from './views/admin/property/building-setup/building-setup.component';
import {SettingsDirectoryComponent} from './views/settings-directory/settings-directory.component';
import {FrontDeskInstructionsComponent} from './views/front-desk-instructions/front-desk-instructions.component';
import {SettingsIntegrationsComponent} from './views/settings-integrations/settings-integrations.component';
import {SettingsKeyLogComponent} from './views/settings-key-log/settings-key-log.component';
import {MaintenanceCalendarComponent} from './views/maintenance-calendar/maintenance-calendar.component';
import {ClassifiedsMccComponent} from './views/classifieds-mcc/classifieds-mcc.component';
import {ResidentPatchResolver} from './resolvers/resitent-patch.resolver';
import {SettingsIncidentReportComponent} from './views/settings-incident-report/settings-incident-report.component';
import {SettingsPassOnLogComponent} from './views/settings-pass-on-log/settings-pass-on-log.component';
import {VersionGuard} from './version.guard';
import {UserStatisticsComponent} from './views/user-statistics/user-statistics.component';
import {StripeAccountsComponent} from './views/stripe-accounts/stripe-accounts.component';
import {SettingsEmailMergeComponent} from './views/settings-email-merge/settings-email-merge.component';
import {SettingsSmsComponent} from './views/settings-sms/settings-sms.component';
import {AnnouncementsComponent} from './views/announcements/announcements.component';
import {AnnouncementsAddComponent} from './views/announcements/announcements-add/announcements-add.component';
import {AnnouncementResolver} from './resolvers/announcement.resolver';
import {UnsavedDataGuard} from './services/guards/unsaved-data-guard.service';
import {AnnouncementRulesResolver} from './resolvers/announcement-rules.resolver';
import {AnnouncementWebsitesResolver} from './resolvers/announcement-websites.resolver';
import {StripeAccountResolver} from './resolvers/stripe-account.resolver';


export const ROUTES: Routes = [
    // Main redirect
    {path: '', redirectTo: 'home', pathMatch: 'full'},

    // App views
    {
        path: '', component: BasicLayoutComponent, canActivate: [AuthGuard], canActivateChild: [VersionGuard],
        children: [
            {path: 'home', component: HomeComponent},
            {path: 'community', loadChildren: './modules/community/community.module#CommunityModule'},
            {path: 'dashboard', loadChildren: './views/dashboard/dashboard.module#DashboardModule'},

            {path: 'directory', component: DirectoryComponent, canActivate: [NavGuard], resolve: {updatesCount: ResidentPatchResolver}, data: {service: 'bldn'}},
            {
                path: 'directory/item/:id', component: DirectoryItemComponent, canActivate: [NavGuard], data: {service: 'bldn'},
                children: [
                    {path: '', redirectTo: 'info', pathMatch: 'full'},
                    {path: 'info', component: InfoComponent},

                    {path: 'residents', component: ResidentsComponent},
                    {path: 'residents/:id', component: ResidentsComponent},

                    {path: 'parking-spots', component: ParkingSpotsComponent},
                    {path: 'parking-spots/:id', component: ParkingSpotsComponent},

                    {path: 'lockers', component: LockersComponent},
                    {path: 'lockers/:id', component: LockersComponent},

                    {path: 'vehicles', component: VehiclesComponent},
                    {path: 'vehicles/:id', component: VehiclesComponent},

                    {path: 'pets', component: PetsComponent},
                    {path: 'pets/:id', component: PetsComponent},

                    {path: 'bike-racks', component: BikeRacksComponent},
                    {path: 'bike-racks/:id', component: BikeRacksComponent},

                    {path: 'activity', redirectTo: 'activity/directory'},
                    {path: 'activity/directory', component: ItemActivityComponent},
                    {path: 'activity/parcels', component: ItemActivityComponent}
                ]
            },
            {path: 'parcels', component: ParcelsComponent, canActivate: [NavGuard], data: {service: 'parcels'}},
            {path: 'incident-reports', component: IncidentReportsComponent, canActivate: [NavGuard], data: {service: 'inc_report'}},
            {path: 'service-request', component: ServiceRequestComponent, canActivate: [NavGuard], data: {service: 'service_request'}},
            {path: 'announcements', component: AnnouncementsComponent, canActivate: [NavGuard], data: {service: 'announcements'}, resolve: {rules: AnnouncementRulesResolver}},
            {
                path: 'announcements/new',
                component: AnnouncementsAddComponent,
                canActivate: [NavGuard, CanCreateGuard],
                canDeactivate: [UnsavedDataGuard],
                data: {service: 'announcements'},
                resolve: {notice: AnnouncementResolver, websites: AnnouncementWebsitesResolver}
            },
            {
                path: 'announcements/edit/:noticeId',
                component: AnnouncementsAddComponent,
                canActivate: [NavGuard, CanCreateGuard],
                canDeactivate: [UnsavedDataGuard],
                data: {service: 'announcements'},
                resolve: {notice: AnnouncementResolver, websites: AnnouncementWebsitesResolver}
            },
            {path: 'key-log', component: KeyLogComponent, canActivate: [NavGuard], data: {service: 'key_log'}},
            {path: 'classifieds', component: ClassifiedsComponent},
            {path: 'notices', component: NoticesComponent},
            {path: 'pass-on-log', component: PassOnLogComponent, canActivate: [NavGuard], data: {service: 'pass_on_log'}},
            {path: 'daily-reports', component: DailyReportsComponent, canActivate: [NavGuard], data: {service: 'daily_report'}},
            {path: 'email-merge', component: EmailComponent, canActivate: [NavGuard], data: {service: 'email_merge'}},
            {path: 'email-merge/compose', component: ComposeEmailComponent, canActivate: [NavGuard], data: {service: 'email_merge'}, canDeactivate: [ConfirmDeactivateGuard]},
            {path: 'documents', component: DocumentsComponent, canActivate: [NavGuard], data: {service: 'documents'}}, // @todo no cancellable requests
            {path: 'amenities', component: AmenitiesComponent, canActivate: [NavGuard], data: {service: 'amenity'}},
            {path: 'front-desk-instructions', component: FrontDeskInstructionsComponent},
            {path: 'parking_passes', component: ParkingPassComponent, canActivate: [NavGuard], data: {service: 'parking_pass'}},
            {path: 'settings', component: SettingsComponent, canActivate: [NavGuard], data: {service: 'amenity'}},
            {path: 'management-company', component: ManagementCompanyComponent, canActivate: [NavGuard], data: {service: 'auth', adminOnly: true}},
            {path: 'property', component: PropertyComponent, canActivate: [NavGuard], data: {service: 'auth'}},
            {path: 'user', component: UserComponent, canActivate: [NavGuard], data: {service: 'auth'}},
            {path: 'buildings', component: SettingsComponent},
            {path: 'themes', component: ThemeListComponent, canActivate: [NavGuard], data: {service: 'auth', adminOnly: true}},
            {path: 'user-role', component: UserRoleComponent, canActivate: [NavGuard], data: {service: 'auth'}},
            {path: 'profile', component: ProfileComponent},
            {path: 'website_builder', component: WebsiteBuilderComponent, canActivate: [NavGuard], data: {service: 'auth'}, canDeactivate: [ExitPageGuard]},
            {path: 'directory_settings', component: SettingsDirectoryComponent, canActivate: [NavGuardSettings], data: {service: 'bldn'}},
            {path: 'amenities_settings', component: SettingsAmenitiesComponent, canActivate: [NavGuardSettings], data: {service: 'amenity'}},
            {path: 'parking_passes_settings', component: SettingsParkingPassesComponent, canActivate: [NavGuardSettings], data: {service: 'parking_pass'}},
            {path: 'parcels_settings', component: SettingsParcelsComponent, canActivate: [NavGuardSettings], data: {service: 'parcels'}},
            {path: 'service-request-settings', loadChildren: './views/settings-service-request/settings-service-request.module#SettingsServiceRequestModule'},
            {path: 'key_log_settings', component: SettingsKeyLogComponent, canActivate: [NavGuardSettings], data: {service: 'key_log'}},
            {path: 'email_merge_settings', component: SettingsEmailMergeComponent, canActivate: [NavGuardSettings], data: {service: 'email_merge'}},
            {path: 'incident_report_settings', component: SettingsIncidentReportComponent, canActivate: [NavGuardSettings], data: {service: 'inc_report'}},
            {path: 'report-generator', component: ReportGeneratorComponent, canActivate: [NavGuard], data: {service: 'report_builder'}, canDeactivate: [ConfirmReportModalDeactivateGuard]},
            {path: 'classifieds-mcc', component: ClassifiedsMccComponent, canActivate: [NavGuard], data: {service: 'report_builder'}},
            {path: 'account-statement', loadChildren: './views/account-statement/account-statement.module#AccountStatementModule'},
            {path: 'integrations_settings', component: SettingsIntegrationsComponent, canActivate: [NavGuard], data: {service: 'amenity'}},
            {path: 'maintenance-calendar', component: MaintenanceCalendarComponent, canActivate: [NavGuard], data: {service: 'amenity'}},
            {path: 'pass_on_log_settings', component: SettingsPassOnLogComponent, canActivate: [NavGuardSettings], data: {service: 'pass_on_log'}},
            {path: 'user-statistics', component: UserStatisticsComponent, canActivate: [NavGuard], data: {service: 'amenity'}},
            {path: 'stripe-accounts', component: StripeAccountsComponent, canActivate: [NavGuardSettings], data: {service: 'amenity'}, resolve: {stripeAccounts: StripeAccountResolver}}, // @todo
            {path: 'settings-sms', component: SettingsSmsComponent, canActivate: [NavGuardSettings], data: {service: 'sms'}},
        ]
    },
    {
        path: '', component: BlankLayoutComponent,
        children: [
            {path: 'building-setup/:id', component: BuildingSetupComponent, canActivate: [NavGuard], data: {service: 'auth'}},
            {path: 'login', component: LoginComponent},
            {path: ':path', component: LoginComponent, canActivate: [LoginGuard]},
        ]
    },

    // Handle all other routes
    {path: '**', redirectTo: 'home'}
];



// WEBPACK FOOTER //
// /app/src/app/app.routes.ts